import {
  NAVIGATION_SESSION_MAX_BYTES,
  NAVIGATION_SESSION_PREFIX,
  NAVIGATION_SESSION_TTL_MS,
} from './sessionStore';

export const NAVIGATION_SCROLL_STORAGE_KEY = `${NAVIGATION_SESSION_PREFIX}scroll`;
export const MAX_SCROLL_IDENTITY_PART_LENGTH = 4_096;

const SCROLL_STORAGE_VERSION = 1;

export type ScrollTarget = 'window' | `named:${string}`;

export interface ScrollIdentity {
  scopeId: string;
  locationKey: string;
  viewKey: string;
  target: ScrollTarget;
}

export interface ScrollPoint {
  x: number;
  y: number;
}

export interface ScrollSnapshot extends ScrollPoint {
  identity: ScrollIdentity;
  updatedAt: number;
}

export interface ScrollRegistry {
  read(identity: ScrollIdentity): ScrollSnapshot | null;
  write(identity: ScrollIdentity, point: ScrollPoint): void;
  remove(identity: ScrollIdentity, replacement?: ScrollIdentity): void;
  purge(scopeId?: string): void;
}

interface StoredScrollEnvelope {
  version: 1;
  entries: ScrollSnapshot[];
}

interface InternalScrollSnapshot extends ScrollSnapshot {
  encodedIdentity: string;
}

function ownKeysExactly(value: object, expected: string[]): boolean {
  let keys: PropertyKey[];
  try {
    keys = Reflect.ownKeys(value);
  } catch {
    return false;
  }
  return keys.length === expected.length && expected.every((key) => keys.includes(key));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  try {
    const prototype = Object.getPrototypeOf(value) as unknown;
    return prototype === Object.prototype || prototype === null;
  } catch {
    return false;
  }
}

function ownDataValue(value: object, key: PropertyKey): unknown {
  let descriptor: PropertyDescriptor | undefined;
  try {
    descriptor = Object.getOwnPropertyDescriptor(value, key);
  } catch {
    return undefined;
  }
  return descriptor && 'value' in descriptor && descriptor.enumerable === true
    ? descriptor.value
    : undefined;
}

function validateIdentityPart(value: unknown, label: string): string {
  if (typeof value !== 'string'
    || value.trim().length === 0
    || value.length > MAX_SCROLL_IDENTITY_PART_LENGTH) {
    throw new Error(`Invalid navigation scroll identity: ${label}`);
  }
  try {
    encodeURIComponent(value);
  } catch {
    throw new Error(`Invalid navigation scroll identity: ${label}`);
  }
  return value;
}

function validateTarget(value: unknown): ScrollTarget {
  if (value === 'window') return value;
  if (typeof value !== 'string' || !value.startsWith('named:')) {
    throw new Error('Invalid navigation scroll identity: target');
  }
  validateIdentityPart(value.slice('named:'.length), 'target');
  return value as ScrollTarget;
}

function snapshotIdentity(identity: ScrollIdentity): ScrollIdentity {
  if (!isRecord(identity)
    || !ownKeysExactly(identity, ['scopeId', 'locationKey', 'viewKey', 'target'])) {
    throw new Error('Invalid navigation scroll identity');
  }
  return Object.freeze({
    scopeId: validateIdentityPart(ownDataValue(identity, 'scopeId'), 'scopeId'),
    locationKey: validateIdentityPart(ownDataValue(identity, 'locationKey'), 'locationKey'),
    viewKey: validateIdentityPart(ownDataValue(identity, 'viewKey'), 'viewKey'),
    target: validateTarget(ownDataValue(identity, 'target')),
  });
}

export function scrollIdentity(
  scopeId: string,
  locationKey: string,
  viewKey: string,
  target: ScrollTarget,
): string {
  const stable = snapshotIdentity({ scopeId, locationKey, viewKey, target });
  return [stable.scopeId, stable.locationKey, stable.viewKey, stable.target]
    .map(encodeURIComponent)
    .join('|');
}

function encodedIdentity(identity: ScrollIdentity): string {
  return scrollIdentity(
    identity.scopeId,
    identity.locationKey,
    identity.viewKey,
    identity.target,
  );
}

function validatePoint(point: ScrollPoint): ScrollPoint {
  if (!isRecord(point) || !ownKeysExactly(point, ['x', 'y'])) {
    throw new Error('Invalid navigation scroll coordinate');
  }
  const x = ownDataValue(point, 'x');
  const y = ownDataValue(point, 'y');
  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    throw new Error('Invalid navigation scroll coordinate');
  }
  return { x: x as number, y: y as number };
}

function byteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

function publicSnapshot(snapshot: InternalScrollSnapshot): ScrollSnapshot {
  return Object.freeze({
    identity: snapshot.identity,
    x: snapshot.x,
    y: snapshot.y,
    updatedAt: snapshot.updatedAt,
  });
}

function defineData(target: object, key: PropertyKey, value: unknown): void {
  Object.defineProperty(target, key, {
    configurable: true,
    enumerable: true,
    value,
    writable: true,
  });
}

function serialize(entries: InternalScrollSnapshot[]): string {
  const safeEntries: ScrollSnapshot[] = [];
  Object.setPrototypeOf(safeEntries, null);
  for (let index = 0; index < entries.length; index += 1) {
    const source = entries[index]!;
    const identity = Object.create(null) as ScrollIdentity;
    defineData(identity, 'scopeId', source.identity.scopeId);
    defineData(identity, 'locationKey', source.identity.locationKey);
    defineData(identity, 'viewKey', source.identity.viewKey);
    defineData(identity, 'target', source.identity.target);
    const snapshot = Object.create(null) as ScrollSnapshot;
    defineData(snapshot, 'identity', identity);
    defineData(snapshot, 'x', source.x);
    defineData(snapshot, 'y', source.y);
    defineData(snapshot, 'updatedAt', source.updatedAt);
    defineData(safeEntries, String(index), snapshot);
  }
  const envelope = Object.create(null) as StoredScrollEnvelope;
  defineData(envelope, 'version', SCROLL_STORAGE_VERSION);
  defineData(envelope, 'entries', safeEntries);
  return JSON.stringify(envelope);
}

function bestEffortRemove(storage: Storage): void {
  try {
    storage.removeItem(NAVIGATION_SCROLL_STORAGE_KEY);
  } catch {
    // Durable state is optional; the bounded in-memory registry remains authoritative.
  }
}

function validateStoredSnapshot(
  value: unknown,
  currentTime: number,
): InternalScrollSnapshot | null {
  if (!isRecord(value)
    || !ownKeysExactly(value, ['identity', 'x', 'y', 'updatedAt'])
    || !isRecord(value.identity)
    || !ownKeysExactly(value.identity, ['scopeId', 'locationKey', 'viewKey', 'target'])
    || !Number.isFinite(value.x)
    || !Number.isFinite(value.y)
    || !Number.isSafeInteger(value.updatedAt)
    || (value.updatedAt as number) < 0
    || (value.updatedAt as number) > currentTime
    || currentTime - (value.updatedAt as number) >= NAVIGATION_SESSION_TTL_MS) {
    return null;
  }
  try {
    const identity = snapshotIdentity(value.identity as unknown as ScrollIdentity);
    return {
      identity,
      encodedIdentity: encodedIdentity(identity),
      x: value.x as number,
      y: value.y as number,
      updatedAt: value.updatedAt as number,
    };
  } catch {
    return null;
  }
}

export function createScrollRegistry(
  storage: Storage,
  now: () => number,
  limit = 50,
): ScrollRegistry {
  if (!Number.isSafeInteger(limit) || limit <= 0) {
    throw new Error('Invalid navigation scroll registry limit');
  }

  let entries: InternalScrollSnapshot[] = [];
  let loaded = false;
  let durableNeedsSanitizing = false;

  const currentTime = (): number => {
    const value = now();
    if (!Number.isSafeInteger(value) || value < 0) {
      throw new Error('Invalid navigation scroll timestamp');
    }
    return value;
  };

  const boundEntries = (
    source: InternalScrollSnapshot[],
    at: number,
  ): InternalScrollSnapshot[] => {
    const unexpired = source.filter((entry) => entry.updatedAt <= at
      && at - entry.updatedAt < NAVIGATION_SESSION_TTL_MS);
    const scopeCounts = new Map<string, number>();
    const withinScopeLimit: InternalScrollSnapshot[] = [];
    for (let index = unexpired.length - 1; index >= 0; index -= 1) {
      const entry = unexpired[index]!;
      const count = scopeCounts.get(entry.identity.scopeId) ?? 0;
      if (count >= limit) continue;
      scopeCounts.set(entry.identity.scopeId, count + 1);
      withinScopeLimit.push(entry);
    }
    withinScopeLimit.reverse();
    while (withinScopeLimit.length > 0
      && byteLength(serialize(withinScopeLimit)) > NAVIGATION_SESSION_MAX_BYTES) {
      withinScopeLimit.shift();
    }
    return withinScopeLimit;
  };

  const load = (at: number): void => {
    if (loaded) return;
    loaded = true;
    let raw: string | null;
    try {
      raw = storage.getItem(NAVIGATION_SCROLL_STORAGE_KEY);
    } catch {
      durableNeedsSanitizing = true;
      return;
    }
    if (raw === null) return;
    if (byteLength(raw) > NAVIGATION_SESSION_MAX_BYTES) {
      durableNeedsSanitizing = true;
      return;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw) as unknown;
    } catch {
      durableNeedsSanitizing = true;
      return;
    }
    if (!isRecord(parsed)
      || !ownKeysExactly(parsed, ['version', 'entries'])
      || parsed.version !== SCROLL_STORAGE_VERSION
      || !Array.isArray(parsed.entries)) {
      durableNeedsSanitizing = true;
      return;
    }
    const byIdentity = new Map<string, InternalScrollSnapshot>();
    let invalid = false;
    for (const item of parsed.entries) {
      const snapshot = validateStoredSnapshot(item, at);
      if (!snapshot) {
        invalid = true;
        continue;
      }
      if (byIdentity.has(snapshot.encodedIdentity)) byIdentity.delete(snapshot.encodedIdentity);
      byIdentity.set(snapshot.encodedIdentity, snapshot);
    }
    const validated = [...byIdentity.values()];
    entries = boundEntries(validated, at);
    durableNeedsSanitizing = invalid || entries.length !== parsed.entries.length;
  };

  const persist = (at: number): void => {
    entries = boundEntries(entries, at);
    if (entries.length === 0) {
      bestEffortRemove(storage);
      durableNeedsSanitizing = false;
      return;
    }
    const serialized = serialize(entries);
    try {
      storage.setItem(NAVIGATION_SCROLL_STORAGE_KEY, serialized);
      durableNeedsSanitizing = false;
    } catch {
      bestEffortRemove(storage);
    }
  };

  const prune = (at: number): boolean => {
    const bounded = boundEntries(entries, at);
    const changed = bounded.length !== entries.length;
    entries = bounded;
    return changed;
  };

  return {
    read(identity: ScrollIdentity): ScrollSnapshot | null {
      const at = currentTime();
      load(at);
      const stableIdentity = snapshotIdentity(identity);
      const key = encodedIdentity(stableIdentity);
      const changed = prune(at);
      const index = entries.findIndex((entry) => entry.encodedIdentity === key);
      if (index === -1) {
        if (changed && !durableNeedsSanitizing) persist(at);
        return null;
      }
      const [snapshot] = entries.splice(index, 1);
      entries.push(snapshot!);
      persist(at);
      return publicSnapshot(snapshot!);
    },

    write(identity: ScrollIdentity, point: ScrollPoint): void {
      const at = currentTime();
      load(at);
      const stableIdentity = snapshotIdentity(identity);
      const stablePoint = validatePoint(point);
      const key = encodedIdentity(stableIdentity);
      entries = entries.filter((entry) => entry.encodedIdentity !== key);
      entries.push({
        identity: stableIdentity,
        encodedIdentity: key,
        x: stablePoint.x,
        y: stablePoint.y,
        updatedAt: at,
      });
      persist(at);
    },

    remove(identity: ScrollIdentity, replacement?: ScrollIdentity): void {
      const at = currentTime();
      load(at);
      const source = snapshotIdentity(identity);
      const sourceKey = encodedIdentity(source);
      const index = entries.findIndex((entry) => entry.encodedIdentity === sourceKey);
      if (index === -1) {
        if (durableNeedsSanitizing) persist(at);
        return;
      }
      const [removed] = entries.splice(index, 1);
      if (replacement) {
        const target = snapshotIdentity(replacement);
        const targetKey = encodedIdentity(target);
        entries = entries.filter((entry) => entry.encodedIdentity !== targetKey);
        entries.push({
          ...removed!,
          identity: target,
          encodedIdentity: targetKey,
        });
      }
      persist(at);
    },

    purge(scopeId?: string): void {
      const at = currentTime();
      load(at);
      if (scopeId === undefined) {
        entries = [];
        bestEffortRemove(storage);
        durableNeedsSanitizing = false;
        return;
      }
      const stableScopeId = validateIdentityPart(scopeId, 'scopeId');
      entries = entries.filter((entry) => entry.identity.scopeId !== stableScopeId);
      persist(at);
    },
  };
}
